/**
 * HelpPopover Component
 *
 * A small circular (?) button that opens a Cloudscape Popover with contextual
 * help text. Drop it inline next to any label or field where users might need
 * guidance.
 *
 * Usage:
 *   <HelpPopover content="Org leader restricts enrollment to users in their reporting chain." />
 *
 * With a header:
 *   <HelpPopover
 *     header="Graduation Approval"
 *     content="Admin approval requires a program admin to review each request before the participant is marked as graduated."
 *   />
 *
 * Rich content (JSX):
 *   <HelpPopover
 *     header="Self Enrollment"
 *     content={<><b>On</b> — participants can enroll themselves during the registration window.<br /><b>Off</b> — only admins can enroll participants.</>}
 *   />
 */

import React from 'react';
import Popover from '@cloudscape-design/components/popover';
import Button from '@cloudscape-design/components/button';

interface HelpPopoverProps {
  /** Main help text shown in the popover body. Accepts a string or JSX for richer content. */
  content: React.ReactNode;
  /** Optional bold header shown above the content. */
  header?: string;
}

export const HelpPopover: React.FC<HelpPopoverProps> = ({ content, header }) => (
  <Popover
    size="large"
    position="right"
    triggerType="custom"
    header={header}
    content={content}
    dismissAriaLabel="Close help"
  >
    <Button
      variant="inline-icon"
      iconName="status-info"
      ariaLabel="Help"
    />
  </Popover>
);
